import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useHeader } from "../../Providers/HeaderContextProvider";
import { updateCoachingMode } from "../../Services/userSettings";
import arisLogo from "../../Assets/images/logo-white.svg";
import activationVoice from "../../Assets/icon/home/activation-voice.svg";
import activationKeyboard from "../../Assets/icon/home/activation-keyboard.svg";
import {
  isActivationPending,
  getCompletedStepsFromStorage,
} from "../../Utils/onboardingNavigation";
import "./talkItThrough.css";

/**
 * The first conversation (Figma 10112:5108): shown instead of "Talk it
 * Through" while the member's activation is pending. Same two doors — the
 * activation conversation is chosen from the member's onboarding_step
 * inside the voice / chat session, not here.
 */
const ACTIVATION_COPY = {
  title: "Welcome to Aris",
  // The design reads "conversion"; "conversation" is what it means (design fix).
  tagline: "Your first conversation is where everything begins",
  body: "Together, we'll get to know one another, explore what matters most in your life, and build your personal Mission, creating the foundation for guidance that's uniquely yours.",
  prompt: "How would you like to begin?",
  promptSub: "Choose voice or text for your first conversation.",
};

/**
 * The Aris tab — Figma node 9384:2925.
 *
 * Replaces the old implicit behaviour where /dashboard read the persisted
 * `coaching_mode` and rendered either the voice or the text home, with the header
 * carrying a switch between them. The choice is now explicit and per-session.
 *
 * The mode is still persisted on choice, so anything else reading
 * `coaching_mode` (onboarding, the profile) keeps seeing the member's latest
 * preference — it just no longer decides what this page renders.
 */
function TalkItThrough() {
  const navigate = useNavigate();
  const { setHeaderContent, setHeaderContentRightSideContent } = useHeader();
  const [busy, setBusy] = useState(null);
  // Read once: activation finishes inside the conversation, and the member
  // leaves this screen to have it.
  const [activating] = useState(() => isActivationPending(getCompletedStepsFromStorage()));

  useEffect(() => {
    setHeaderContent(true);
    setHeaderContentRightSideContent(true);
  }, [setHeaderContent, setHeaderContentRightSideContent]);

  // Both choices land straight in a conversation, never on an intermediate
  // landing screen.
  //
  // Voice runs on /dashboard?mode=voice, which auto-starts the session. Text goes
  // to /chat — the conversation view itself — rather than DashboardChat, whose
  // greeting-and-prompt screen is the extra step we are removing. Both keep the
  // browser back button pointed at this chooser.
  const DESTINATIONS = {
    voice: "/dashboard?mode=voice",
    keyboard: "/chat",
  };

  const choose = async (mode) => {
    setBusy(mode);
    // Persisting is best-effort: a failed write must not block the member from
    // starting the session they asked for.
    try {
      await updateCoachingMode(mode);
    } catch (e) {
      console.error("Failed to persist the coaching mode", e);
    }
    navigate(DESTINATIONS[mode]);
  };

  if (activating) {
    return (
      <div className="tit tit--activation">
        <div className="tit__inner">
          <img src={arisLogo} alt="Aris" className="tit__logo tit__logo--activation" />
          <h1 className="tit__title">{ACTIVATION_COPY.title}</h1>
          <p className="tit__tagline">{ACTIVATION_COPY.tagline}</p>
          <p className="tit__body">{ACTIVATION_COPY.body}</p>
          <h2 className="tit__prompt">{ACTIVATION_COPY.prompt}</h2>
          <p className="tit__prompt-sub">{ACTIVATION_COPY.promptSub}</p>
          <div className="tit__options tit__options--activation">
            <button
              type="button"
              className="tit__option"
              onClick={() => choose("voice")}
              disabled={busy !== null}
            >
              <span className="tit__circle tit__circle--large">
                <img src={activationVoice} alt="" width="59" height="48" />
              </span>
              Voice
            </button>
            <button
              type="button"
              className="tit__option"
              onClick={() => choose("keyboard")}
              disabled={busy !== null}
            >
              <span className="tit__circle tit__circle--large">
                <img src={activationKeyboard} alt="" width="48" height="48" />
              </span>
              Text
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tit">
      <div className="tit__inner">
        <img src={arisLogo} alt="Aris" className="tit__logo" />

        <h1 className="tit__title">Talk it Through</h1>
        <p className="tit__subtitle">Talk with Aris using voice or text</p>

        <div className="tit__options">
          <button
            type="button"
            className="tit__option"
            onClick={() => choose("voice")}
            disabled={busy !== null}
          >
            <span className="tit__circle">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M4 10v4M8 6.5v11M12 4v16M16 7.5v9M20 10.5v3"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            Voice
          </button>

          <button
            type="button"
            className="tit__option"
            onClick={() => choose("keyboard")}
            disabled={busy !== null}
          >
            <span className="tit__circle">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect
                  x="2.5"
                  y="6.5"
                  width="19"
                  height="11"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="1.7"
                />
                <path
                  d="M6 10h.01M9 10h.01M12 10h.01M15 10h.01M18 10h.01M6 13h.01M18 13h.01M9 13.5h6"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            Text
          </button>
        </div>
      </div>
    </div>
  );
}

export default TalkItThrough;
